import { logs, SeverityNumber, type Logger } from '@opentelemetry/api-logs';
import {
  BatchLogRecordProcessor,
  LoggerProvider,
} from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  getEnvironment,
  oneIdentifier,
  type Environment,
} from '~shared/config/hostname';
import { enrichAxiosError } from './axios-enrichment';
import { enrichApiError } from './api-error-enrichment';
import { redactUrl } from './url-redaction';

// Only these two environments have a collector. Everything else — local, dev,
// and any hostname we do not recognise — resolves to undefined, no exporter is
// constructed, and nothing is ever sent.
const ENDPOINTS: Partial<Record<Environment, string>> = {
  production: 'https://signoz-ingest.samera.io/v1/logs',
  staging: 'https://signoz-ingest.staging.samera.io/v1/logs',
};

// Guest sessions are short. The SDK default (~5s) loses everything when a
// guest errors and bounces, or is handed off to the payment provider, before
// the first batch is scheduled.
const SCHEDULED_DELAY_MS = 1000;

// Bounds what one browser can send. An error thrown inside a render loop would
// otherwise emit continuously.
const MAX_RECORDS_PER_SESSION = 50;

// The response interceptor in api.instance.ts falls back to `JSON.stringify` of
// the response body when it does not match our schema, and that string becomes
// the error's message. Left unbounded it would carry a proxy error page or an
// echoed request into the log store verbatim, and make every occurrence a
// distinct message — which also defeats the dedupe below.
const MAX_MESSAGE_LENGTH = 500;

// Nothing else needs to be big either. Generous enough to keep a usable stack
// trace, small enough that one record cannot dominate a batch.
const MAX_ATTRIBUTE_LENGTH = 4096;

const truncate = (value: string, max: number): string =>
  value.length > max ? `${value.slice(0, max)}…[truncated]` : value;

// Fraction of sessions that emit a boot record. Without it, "no errors" and
// "telemetry is broken" are the same reading: every failure in this module is
// swallowed by design, so silence would otherwise be unfalsifiable.
const HEARTBEAT_SAMPLE_RATE = 0.05;

const IGNORE_ERRORS = [
  // Outlook SafeLinks pre-scanner (Microsoft) probes promise behavior in a way
  // that throws this signature. Not a real bug in our code.
  /^Object Not Found Matching Id:/,
];

const DENY_URLS = [
  // Pinterest conversion tracking script — periodic load failures from CDN
  // issues, network blocking, or ad blockers.
  /ct\.pinterest\.com/,
];

// `document.referrer` is empty for a typed URL, a bookmark, a QR code, an
// in-app browser, an HTTPS→HTTP downgrade, and a referring site that suppressed
// it — we cannot tell those apart, so this does NOT claim "direct". Emitted
// explicitly rather than omitted so the case is a bucket you can count instead
// of a blank that reads as a missing attribute. Parenthesised so it can never
// be mistaken for a URL.
const NO_REFERRER = '(none)';

/**
 * The origin a visitor arrived from — for an http(s) referrer, never the path
 * or the query.
 *
 * The referring site picks its own `Referrer-Policy`, so "the browser has
 * already reduced it to an origin" is that site's convention and not our
 * guarantee: one sending `unsafe-url` hands us its full URL, and `redactUrl`
 * cannot help there — its path masks and query allowlist describe samera-one's
 * own routes, not a stranger's, so a foreign identifier would pass straight
 * through. The origin is the whole of what "where did this visitor come from"
 * needs, and dropping the rest removes that class outright rather than chasing
 * patterns. It bounds cardinality to roughly the number of sites that link to
 * a hotel, too.
 *
 * A non-special scheme has no origin — `url.origin` is the literal string
 * `null` — so it falls back to `protocol//host`, which keeps every
 * `android-app://…` webmail and in-app-browser arrival as its own bucket
 * instead of collapsing the lot. That host is an *opaque* one: it cannot hold
 * a literal `/ ? #` but a percent-encoded one survives, so the no-path promise
 * above is a guarantee for http(s) and a convention for the rest. Bounded
 * either way, since `URL` will parse a host of any length.
 */
const MAX_REFERRER_LENGTH = 512;

const originOf = (raw: string): string => {
  try {
    const url = new URL(raw);
    const origin =
      url.origin !== 'null'
        ? url.origin
        : url.host
        ? `${url.protocol}//${url.host}`
        : url.protocol;
    return truncate(origin, MAX_REFERRER_LENGTH);
  } catch {
    // Not something a browser produces. Folded in with the referrer-less
    // entries rather than given a value of its own, which would only add a
    // second empty-looking bucket.
    return NO_REFERRER;
  }
};

// Read once: the referrer belongs to the document, and an SPA route change does
// not update it.
const referrer = document.referrer ? originOf(document.referrer) : NO_REFERRER;

interface ObservabilityContext {
  route?: string;
}

let loggerProvider: LoggerProvider | undefined;
let logger: Logger | undefined;
let context: ObservabilityContext = {};
let inFlight = false;
let recordCount = 0;
const seen = new Set<string>();

const init = (): void => {
  const environment = getEnvironment();
  const endpoint = ENDPOINTS[environment];
  if (!endpoint) return;

  try {
    loggerProvider = new LoggerProvider({
      resource: resourceFromAttributes({
        'service.name': 'samera-one',
        'service.version': __BUILD_HASH__,
        'deployment.environment': environment,
        // The hotel is the subdomain, so it is fixed for the lifetime of the
        // document and belongs on the resource. `browser.user_agent`
        // deliberately does not: resource attributes identify the emitting
        // service, and a per-browser value there would multiply resource
        // cardinality against every hotel slug. It goes on the record instead.
        'hotel.slug': oneIdentifier,
      }),
      processors: [
        new BatchLogRecordProcessor(new OTLPLogExporter({ url: endpoint }), {
          scheduledDelayMillis: SCHEDULED_DELAY_MS,
        }),
      ],
    });
    logs.setGlobalLoggerProvider(loggerProvider);
    logger = logs.getLogger('samera-one');
  } catch {
    loggerProvider = undefined;
    logger = undefined;
  }
};

const normalizeError = (
  error: unknown,
): { name: string; message: string; stack?: string } => {
  const normalized = ((): { name: string; message: string; stack?: string } => {
    if (error instanceof Error) {
      return {
        name: error.name || 'Error',
        message: error.message,
        stack: error.stack,
      };
    }
    if (typeof error === 'string') return { name: 'Error', message: error };
    if (error && typeof error === 'object') {
      const e = error as { name?: unknown; message?: unknown; stack?: unknown };
      return {
        name: typeof e.name === 'string' ? e.name : 'Error',
        message:
          typeof e.message === 'string' ? e.message : safeStringify(error),
        stack: typeof e.stack === 'string' ? e.stack : undefined,
      };
    }
    return { name: 'Error', message: String(error) };
  })();

  // Bounded here rather than at the emit site so the dedupe key below is bounded
  // too — an unbounded message makes every occurrence look distinct.
  return {
    ...normalized,
    message: truncate(normalized.message, MAX_MESSAGE_LENGTH),
  };
};

// JSON.stringify throws on circular structures, which would otherwise take the
// whole record down via the outer catch.
const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return '[unserializable]';
  }
};

const compactAttributes = (
  attrs: Record<string, unknown>,
): Record<string, string | number | boolean> => {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    } else if (typeof value === 'string') {
      out[key] = truncate(value, MAX_ATTRIBUTE_LENGTH);
    } else {
      out[key] = truncate(safeStringify(value), MAX_ATTRIBUTE_LENGTH);
    }
  }
  return out;
};

const shouldIgnore = (
  message: string,
  extra: Record<string, unknown>,
): boolean => {
  if (IGNORE_ERRORS.some(pattern => pattern.test(message))) return true;

  const source = extra['exception.source'];
  return (
    typeof source === 'string' &&
    DENY_URLS.some(pattern => pattern.test(source))
  );
};

// SigNoz has no fingerprint concept, so grouping happens at query time on this
// attribute. Applied inside logError rather than only at the call site because
// `transformOfferToAddToCartDto` reports AND re-throws, so the rethrow arrives
// separately and would otherwise group by its per-failure-mode message.
const fingerprintFor = (message: string): string | undefined => {
  if (message.startsWith('Invalid offer:')) return 'invalid-offer';
  // MoneyContractError from assert-money, same report-and-rethrow shape: the
  // mapper reports and the throw arrives again on its own, and the message
  // carries the failing field ("Invalid money response (lastBooking.total): …"),
  // so every field would otherwise be its own group.
  if (message.startsWith('Invalid money response')) return 'money-contract';
  return undefined;
};

const emit = (
  body: string,
  severityNumber: SeverityNumber,
  severityText: string,
  attributes: Record<string, unknown>,
): void => {
  logger?.emit({
    body,
    severityNumber,
    severityText,
    attributes: compactAttributes({
      ...attributes,
      'browser.user_agent': navigator.userAgent,
      // On the heartbeat too, not only on errors: the heartbeat is the sampled
      // denominator, so without it a referrer can be counted but never rated.
      'page.referrer': referrer,
    }),
  });
};

export const setObservabilityContext = (patch: ObservabilityContext): void => {
  context = { ...context, ...patch };
};

export const logError = (
  error: unknown,
  extra: Record<string, unknown> = {},
): void => {
  if (!logger || inFlight) return;
  inFlight = true;

  try {
    const normalized = normalizeError(error);
    if (shouldIgnore(normalized.message, extra)) return;
    if (recordCount >= MAX_RECORDS_PER_SESSION) return;

    // `kind` is part of the key so the error boundary's record — the only one
    // carrying a component stack — is never dropped in favour of the global
    // handler's copy of the same error, whichever fires first.
    //
    // The route is part of it for a subtler reason: without it, the first route
    // to hit a given error owns that error for the rest of the session, and
    // every later occurrence elsewhere is silently dropped. `page.route` then
    // reports where the error was seen *first*, not where it happens — which
    // reads as "this only breaks on /booking/" for something that breaks
    // everywhere. Grouping by route in SigNoz inherits that bias directly.
    // `MAX_RECORDS_PER_SESSION` still bounds what this costs.
    const key = `${normalized.name}|${normalized.message}|${String(
      extra.kind ?? '',
    )}|${context.route ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    recordCount += 1;

    emit(normalized.message, SeverityNumber.ERROR, 'ERROR', {
      // Caller attributes come first so they can never overwrite the canonical
      // fields below.
      ...extra,
      ...enrichAxiosError(error),
      ...enrichApiError(error),
      'exception.type': normalized.name,
      'exception.message': normalized.message,
      'exception.stacktrace': normalized.stack,
      'page.url': redactUrl(window.location.href),
      'page.route': context.route,
      'error.fingerprint': fingerprintFor(normalized.message),
    });
  } catch {
    // Never let the logger crash the app.
  } finally {
    inFlight = false;
  }
};

export const flushLogger = async (timeoutMs = 500): Promise<void> => {
  if (!loggerProvider) return;
  try {
    await Promise.race([
      loggerProvider.forceFlush(),
      new Promise<void>(resolve => setTimeout(resolve, timeoutMs)),
    ]);
  } catch {
    // ignore
  }
};

const emitHeartbeat = (): void => {
  if (!logger) return;
  if (Math.random() >= HEARTBEAT_SAMPLE_RATE) return;
  try {
    emit('samera-one boot', SeverityNumber.INFO, 'INFO', { kind: 'heartbeat' });
  } catch {
    // ignore
  }
};

window.addEventListener('error', event => {
  logError(event.error ?? event.message, {
    kind: 'window.onerror',
    // Redacted like any other URL: the failing script can be a third party we
    // hand a URL to — the PSP widget in payment-dialog.tsx is loaded from the
    // backend-supplied `paymentUrl` — and its query string is not ours to
    // export. `DENY_URLS` still matches, because redaction keeps the origin and
    // path and only drops the query, fragment and path identifiers.
    'exception.source': redactUrl(event.filename),
    'exception.lineno': event.lineno,
    'exception.colno': event.colno,
  });
});

window.addEventListener('unhandledrejection', event => {
  logError(event.reason, { kind: 'unhandledrejection' });
});

// Best-effort only: the browser may terminate execution before an async flush
// completes. The awaited flush in `handlePayment` is what actually protects the
// payment handoff; this covers tab closes and back-navigation.
const flushOnHide = (): void => {
  void flushLogger(100);
};

window.addEventListener('pagehide', flushOnHide);
window.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') flushOnHide();
});

init();
emitHeartbeat();
